import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';

// PrimeNG Imports
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { MessagesModule } from 'primeng/messages';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-dashboard-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    CardModule,
    InputTextModule,
    ButtonModule,
    PasswordModule,
    MessagesModule,
    ToastModule
  ],
  providers: [MessageService],
  template: `
    <div class="login-container">
      <div class="login-header">
        <div class="nzf-logo">
          <a href="https://nzf.org.au" target="_blank">
            <img src="assets/nzf.png" alt="NZF Logo" class="logo-img">
          </a>
        </div>
      </div>
      
      <div class="login-form-container">
        <p-card class="login-card">
          <div class="login-content">
            <div class="lock-icon">
              <i class="pi pi-lock" style="font-size: 3rem; color: #6366f1;"></i>
            </div>
            
            <h2 class="login-title">Dashboard Access</h2>
            <p class="login-description">
              Enter the dashboard password to access assessment results
            </p>
            
            <form (ngSubmit)="onLogin()" #loginForm="ngForm" class="login-form">
              <div class="form-group">
                <label for="password" class="form-label">Password</label>
                <p-password
                  id="password"
                  [(ngModel)]="password"
                  name="password"
                  [feedback]="false"
                  [toggleMask]="true"
                  placeholder="Enter dashboard password"
                  styleClass="w-full"
                  inputStyleClass="w-full"
                  [disabled]="loading"
                  required>
                </p-password>
              </div>
              
              <div class="form-actions">
                <p-button
                  type="submit"
                  label="Access Dashboard"
                  icon="pi pi-sign-in"
                  [loading]="loading"
                  [disabled]="!password || password.length < 1"
                  styleClass="w-full">
                </p-button>
              </div>
              
              <div class="error-message" *ngIf="errorMessage">
                <i class="pi pi-exclamation-triangle"></i>
                {{ errorMessage }}
              </div>
            </form>
            
            <div class="back-link">
              <a routerLink="/" class="back-button">
                <i class="pi pi-arrow-left"></i>
                Back to Assessment
              </a>
            </div>
          </div>
        </p-card>
      </div>
      
      <p-toast></p-toast>
    </div>
  `,
  styles: [`
    .login-container {
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, rgb(251 22 54) 100%);
      display: flex;
      flex-direction: column;
      padding: 1rem;
    }

    .login-header {
      text-align: center;
      padding: 1rem;
    }

    .logo-img {
      width: min(150px, 60vw);
      max-width: 150px;
      height: auto;
    }

    .login-form-container {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2rem 0;
    }

    .login-card {
      width: 100%;
      max-width: 400px;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(10px);
      border-radius: 20px;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    }

    .login-content {
      text-align: center;
      padding: 1rem;
    }

    .lock-icon {
      margin-bottom: 1.5rem;
    }

    .login-title {
      font-size: clamp(1.5rem, 4vw, 2rem);
      font-weight: 700;
      color: #1a202c;
      margin-bottom: 0.5rem;
    }

    .login-description {
      font-size: clamp(0.875rem, 2.5vw, 1rem);
      color: #4a5568;
      margin-bottom: 2rem;
      line-height: 1.5;
    }

    .login-form {
      text-align: left;
    }

    .form-group {
      margin-bottom: 1.5rem;
    }

    .form-label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 600;
      color: #374151;
      font-size: 0.875rem;
    }

    .form-actions {
      margin-bottom: 1rem;
    }

    .error-message {
      background: #fef2f2;
      border: 1px solid #fecaca;
      color: #dc2626;
      padding: 0.75rem;
      border-radius: 8px;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      margin-top: 1rem;
    }

    .back-link {
      margin-top: 1.5rem;
      padding-top: 1.5rem;
      border-top: 1px solid #e5e7eb;
    }

    .back-button {
      color: #6366f1;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      transition: color 0.2s ease;
    }

    .back-button:hover {
      color: #4f46e5;
      text-decoration: underline;
    }

    /* Responsive adjustments */
    @media (max-width: 480px) {
      .login-form-container {
        padding: 1rem 0;
      }

      .login-card {
        margin: 0 0.5rem;
      }

      .login-content {
        padding: 0.5rem;
      }
    }

    /* PrimeNG customizations */
    :host ::ng-deep .p-password {
      width: 100%;
    }

    :host ::ng-deep .p-password input {
      width: 100%;
      padding: 0.75rem;
      border: 2px solid #e2e8f0;
      border-radius: 8px;
      font-size: 1rem;
    }

    :host ::ng-deep .p-password input:focus {
      border-color: #6366f1;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }

    :host ::ng-deep .p-button {
      padding: 0.75rem 1.5rem;
      font-size: 1rem;
      font-weight: 600;
      border-radius: 8px;
      background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
      border: none;
      transition: all 0.2s ease;
    }

    :host ::ng-deep .p-button:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
    }

    :host ::ng-deep .p-button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    :host ::ng-deep .w-full {
      width: 100% !important;
    }
  `]
})
export class DashboardLoginComponent {
  @Output() loginSuccess = new EventEmitter<void>();
  
  password: string = '';
  loading: boolean = false;
  errorMessage: string = '';

  constructor(
    private authService: AuthService,
    private messageService: MessageService
  ) {}

  async onLogin(): Promise<void> {
    if (!this.password) {
      this.errorMessage = 'Please enter a password';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      const isAuthenticated = await this.authService.authenticateWithPassword(this.password);
      
      if (isAuthenticated) {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Authentication successful'
        });
        this.loginSuccess.emit();
      } else {
        this.errorMessage = 'Invalid password. Please try again.';
        this.password = ''; // Clear password field
      }
    } catch (error) {
      console.error('Login error:', error);
      this.errorMessage = 'An error occurred during authentication. Please try again.';
    } finally {
      this.loading = false;
    }
  }
}
